import { CommentIcon, PinIcon, PlayIcon } from "@genuin/ui/icons";
import { Image } from "@genuin/ui/image";
import { cn } from "@genuin/ui/utils";
import { Skeleton } from "@genuin/ui/skeleton";
import { cva } from "class-variance-authority";
import { DialogClose } from "@genuin/ui/components/dialog";

import { Link } from "@genuin/components/molecules/link";
import { Stats } from "../stats";

import type { PostTileProps } from "./post-tile.type";
import { DynamicReactionIcon } from "../reaction-button";
import { useDeviceDetectMediaQuery } from "@genuin/components/hooks/use-devide-detect-media-query";

export const postTileVariants = cva(
  "gencl:group gencl:relative gencl:rounded-md gencl:overflow-hidden gencl:aspect-reel",
  {
    variants: {
      size: {
        sm: "gencl:w-40 sm:gencl:w-44 md:gencl:w-48",
        lg: "gencl:w-44 sm:gencl:w-48 md:gencl:w-52",
      },
      variant: {
        default: "",
        responsive: "",
      },
    },
    compoundVariants: [
      {
        size: "sm",
        variant: "responsive",
        className: "gencl:w-32 gencl:sm:w-40 gencl:md:w-44 gencl:lg:w-48",
      },
      {
        size: "lg",
        variant: "responsive",
        className: "gencl:w-36 gencl:sm:w-44 gencl:md:w-48 gencl:lg:w-52",
      },
    ],
    defaultVariants: {
      size: "sm",
      variant: "default",
    },
  }
);

export function PostTile({
  postData: { imageUrl, url, isPinned, linkouts, stats },
  imageCompProps,
  showHover = true,
  size = "sm",
  variant = "default",
  className,
  onClick,
  shouldCloseModal = false,
  ...restProps
}: PostTileProps & { shouldCloseModal?: boolean }) {
  const { isLargeDesktop, isDesktop } = useDeviceDetectMediaQuery();
  const content = (
    <div
      className={cn(postTileVariants({ size, variant }), className)}
      onClick={onClick}
      {...restProps}
    >
      {imageUrl ? (
        <Image
          aspectRatio="reel"
          className="gencl:bg-secondary-200"
          src={imageUrl}
          {...imageCompProps}
        />
      ) : (
        <div className="gencl:w-full gencl:h-full gencl:bg-secondary-50" />
      )}
      {isPinned && (
        <PinIcon className="gencl:stroke-white gencl:group-hover:hidden gencl:absolute gencl:top-2 gencl:right-2 gencl:fill-white" />
      )}
      {linkouts && (
        <div className="gencl:bg-white gencl:group-hover:hidden gencl:rounded-lg gencl:absolute gencl:top-2 gencl:size-9 gencl:left-2"></div>
      )}
      {stats && (
        <>
          {variant === "responsive" && (
            <Stats
              className={cn(
                "gencl:group-hover:hidden gencl:flex gencl:gap-2 gencl:justify-between gencl:p-2 gencl:absolute gencl:bottom-0 gencl:w-full gencl:bg-linear-to-t gencl:from-black/40 gencl:to-black/0"
              )}
              valueClassName="gencl:text-white! gencl:text-body-2-medium"
              pairClassName="gencl:gap-1"
              stats={{
                Views: {
                  value: stats.views,
                  icon: (
                    <PlayIcon className="gencl:stroke-white gencl:shrink-0 gencl:stroke-2 gencl:size-4 gencl:fill-none" />
                  ),
                },
                Reactions: {
                  value: stats.reactions,
                  icon: (
                    <DynamicReactionIcon
                      sparkCount={0}
                      isSparked={false}
                      iconHeight={16}
                      iconWidth={16}
                      theme="dark"
                    />
                  ),
                },
                ...((isLargeDesktop || isDesktop) && {
                  Comments: {
                    value: stats.comments,
                    icon: (
                      <CommentIcon className="gencl:stroke-white! gencl:shrink-0 gencl:stroke-2 gencl:size-4" />
                    ),
                  },
                }),
              }}
            />
          )}
          {variant === "default" && (
            <Stats
              className={cn(
                "gencl:group-hover:hidden gencl:flex gencl:gap-2 gencl:justify-between gencl:p-2 gencl:absolute gencl:bottom-0 gencl:w-full gencl:bg-linear-to-b gencl:from-transparent gencl:via-black/50 gencl:to-black/50"
              )}
              valueClassName="gencl:text-white! gencl:text-body-2-medium"
              pairClassName="gencl:gap-1"
              stats={{
                Views: {
                  value: stats.views,
                  icon: (
                    <PlayIcon className="gencl:stroke-white gencl:stroke-2 gencl:size-3 gencl:fill-none" />
                  ),
                },
              }}
            />
          )}
        </>
      )}
      {showHover && (
        <div className="gencl:hidden gencl:cursor-pointer gencl:group-hover:flex gencl:items-center gencl:justify-center gencl:absolute gencl:h-full gencl:w-full gencl:inset-0 gencl:bg-black/40">
          {/* <ThreeDotsIcon className="gencl:absolute gencl:top-2 gencl:p-1 gencl:rounded-md gencl:right-2 gencl:stroke-white gencl:bg-black/40" /> */}
          <div className="gencl:p-2.5 gencl:rounded-full gencl:bg-black/40 gencl:backdrop:blur-[3px]">
            <PlayIcon theme="fill-dark" size="md" />
          </div>
        </div>
      )}
    </div>
  );

  // Priority 1: If URL is provided, wrap with Link for navigation
  if (url) {
    const linkContent = <Link href={url}>{content}</Link>;
    if (shouldCloseModal) {
      return <DialogClose asChild>{linkContent}</DialogClose>;
    }
    return linkContent;
  }

  // Priority 2: If onClick is provided, use content as is
  if (onClick) {
    const finalContent = content;
    if (shouldCloseModal) {
      return <DialogClose asChild>{finalContent}</DialogClose>;
    }
    return finalContent;
  }

  // Fallback: Return content without any wrapper
  const finalContent = content;
  if (shouldCloseModal) {
    return <DialogClose asChild>{finalContent}</DialogClose>;
  }
  return finalContent;
}

export function PostTileSkeleton({
  size = "sm",
  className,
}: {
  size: PostTileProps["size"];
  className?: string;
}) {
  return (
    <Skeleton
      className={cn(
        postTileVariants({ size }),
        "gencl:w-full gencl:aspect-reel",
        className
      )}
    />
  );
}
